import React from 'react';
import {
  Box,
  Text,
  TextInput,
  SelectInput,
  Radio,
  RadioGroup,
  DatePicker,
  ActionList,
  ActionListItem,
  Dropdown,
  DropdownOverlay,
} from '@razorpay/blade/components';
import { CustomFieldInputProps, CustomFieldsFormProps, canonicalValueType } from './types';
import { ResetBladePortalOverridingStyles } from 'components/RunPayroll/ComplianceActions/styles';
import { sanitizeFieldInput, toLocalDateString } from './validation';
import { enumOptions, formatFieldValue, getEditableFields } from './utils';

// Re-exported for existing consumers/tests; the implementation lives in utils.ts.
export { getEditableFields };

const FieldInput = ({ def, value, onChange, errorText }: CustomFieldInputProps) => {
  // Blade renders the styled (red) required asterisk via isRequired +
  // necessityIndicator — same mechanism as PersonalDetails' Gender/DOB.
  const label = def.label ?? def.fieldKey ?? '';
  const requiredProps = def.isRequired
    ? ({ isRequired: true, necessityIndicator: 'required' } as const)
    : {};
  const key = def.fieldKey ?? '';
  const validationState = errorText ? 'error' : 'none';

  // FILE fields have no upload/preview UX on this surface yet. Render a disabled,
  // clearly-labeled input instead of a bare text field that looks editable.
  if (canonicalValueType(def.valueType) === 'FILE') {
    return (
      <TextInput
        label={label}
        {...requiredProps}
        value=""
        isDisabled
        placeholder="File upload isn't supported here yet."
        onChange={() => {}}
      />
    );
  }

  switch (canonicalValueType(def.valueType)) {
    case 'BOOLEAN':
      // Same Yes/No radio presentation as the settings create-flow field preview
      // (AddEmployeeFieldModal renderInteractivePreview).
      return (
        <RadioGroup
          label={label}
          {...requiredProps}
          name={`custom-field-${key}`}
          value={value === 'true' || value === '1' ? 'true' : value === 'false' || value === '0' ? 'false' : ''}
          onChange={({ value: v }) => onChange(key, v)}
          validationState={validationState}
          errorText={errorText}
          orientation="horizontal">
          <Radio value="true">Yes</Radio>
          <Radio value="false">No</Radio>
        </RadioGroup>
      );
    case 'DATE':
      return (
        <DatePicker
          label={label}
          {...requiredProps}
          value={value ? new Date(value) : undefined}
          // Local date parts, NOT toISOString(): UTC conversion shifts IST
          // midnight to the previous calendar day (off-by-one selection).
          onChange={(d) => onChange(key, d ? toLocalDateString(d as Date) : null)}
          validationState={validationState}
          errorText={errorText}
        />
      );
    case 'ENUM':
      return (
        <Dropdown selectionType="single">
          <SelectInput
            label={label}
            {...requiredProps}
            value={value ?? undefined}
            onChange={({ values }) => onChange(key, values[0] ?? null)}
            validationState={validationState}
            errorText={errorText}
          />
          <DropdownOverlay>
            <ActionList>
              {enumOptions(def).map((opt) => (
                <ActionListItem key={opt.key} title={opt.label} value={opt.key} />
              ))}
            </ActionList>
          </DropdownOverlay>
        </Dropdown>
      );
    case 'INTEGER':
    case 'DECIMAL':
    case 'CURRENCY':
      return (
        <TextInput
          label={label}
          {...requiredProps}
          type="number"
          value={value ?? ''}
          // type="number" only hints the keyboard — sanitize so typed/pasted
          // text never lands in numeric fields.
          onChange={({ value: v }) => onChange(key, v ? sanitizeFieldInput(def, v) : null)}
          validationState={validationState}
          errorText={errorText}
        />
      );
    default:
      return (
        <TextInput
          label={label}
          {...requiredProps}
          value={value ?? ''}
          onChange={({ value: v }) => onChange(key, v ?? null)}
          validationState={validationState}
          errorText={errorText}
        />
      );
  }
};

export const CustomFieldsForm = ({
  definitions,
  values,
  onChange,
  violations,
  showViewFieldsReadOnly,
}: CustomFieldsFormProps) => {
  const errorFor = (fieldKey: string | null): string | undefined => {
    const violation = violations.find((v) => v.field === fieldKey && v.severity === 'ERROR');
    return violation?.description ?? undefined;
  };

  // Legacy global table/typography CSS bleeds into Blade's DatePicker portal
  // dialog; the same reset RunPayroll's HRIS filter uses fixes it.
  const hasDateField = definitions.some(
    (def) => def.effectiveAccess === 'EDIT' && canonicalValueType(def.valueType) === 'DATE',
  );

  return (
    <Box display="flex" flexDirection="column" gap="spacing.5">
      {hasDateField && <ResetBladePortalOverridingStyles />}
      {definitions.map((def) => {
        if (def.effectiveAccess === 'HIDDEN') return null;
        if (def.effectiveAccess === 'VIEW') {
          if (!showViewFieldsReadOnly) return null;
          return (
            <Box key={def.fieldKey ?? ''} display="flex" justifyContent="space-between" paddingY="spacing.3">
              <Text size="medium" color="surface.text.gray.muted">
                {def.label}
              </Text>
              <Text size="medium">{formatFieldValue(def, values[def.fieldKey ?? ''])}</Text>
            </Box>
          );
        }
        return (
          <FieldInput
            key={def.fieldKey ?? ''}
            def={def}
            value={values[def.fieldKey ?? ''] ?? null}
            onChange={onChange}
            errorText={errorFor(def.fieldKey)}
          />
        );
      })}
    </Box>
  );
};
